import React, { useCallback, ReactNode, Context, useRef, useMemo } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { uuid } from 'uuidv4';
import { useSelector } from '@xstate/react';
import { State } from 'xstate';
import _isEqual from 'lodash/isEqual';
import S3 from 'aws-sdk/clients/s3';

import { queryStrings } from '@trqk/resolvers';
import {
    constants,
    generateCSVFileKeyForS3,
    getWebpackEnvVar,
    hasAcceptedFileType,
    locale,
} from '@trqk/utils';

import { useTasks, useToast, useTokenModal } from '..';
import { AddedWrongFileType } from '../../components/toastMessages';
import { TASK_GROUP_ID_DETECTION_UPLOADS } from '../../constants';

const { SUPPORTED_DETECTION_FILE_TYPES, MAX_DETECTION_UPLOAD_BATCH_SIZE } =
    constants;
const { GET_SESSION, TASK_UPLOAD_DETECTIONS_TO_TRQK_GAP } = queryStrings;

interface UploadDetectionsProviderProps {
    children: ReactNode;
    UploadDetectionsContext: Context<UploadDetectionsProviderValue>;
}

/**
 * A hook that allows the user to upload one or more detection files.
 * @param props - {@link UploadDetectionsProviderProps}
 * @returns JSX
 */
export const UploadDetectionsProvider = ({
    children,
    UploadDetectionsContext,
}: UploadDetectionsProviderProps): JSX.Element => {
    const bucketName = getWebpackEnvVar(
        'web',
        'AWS_S3_GAP_DETECTION_UPLOADS_BUCKET',
    );
    const bucketRegion = getWebpackEnvVar('web', 'AWS_REGION');
    const identityPoolId = getWebpackEnvVar('web', 'AWS_IDENTITY_POOL_ID');
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { sendTaskEvent, taskMachineService } = useTasks();
    const { add: addToast } = useToast();
    const { data: sessionData } = useQuery(GET_SESSION);
    const userId = sessionData?.session?.currentUser?.id;

    const { cache } = useApolloClient();

    const [taskUploadDetectionsToTrqkGap] = useMutation(
        TASK_UPLOAD_DETECTIONS_TO_TRQK_GAP,
    );

    // Each upload results in a ManagedUpload instance that we will need access to in order to
    // allow the user to cancel the upload. We use a ref to maintain access to a single object
    // that contains each of the ManagedUpload instances.
    const managedUploads = useRef({} as ManagedUploadsRef);
    // onTaskCancel is bound to the 'CANCEL' event when the task is started. This allows us to
    // call: `sendTaskEvent({ type: 'CANCEL', value: { taskId }})` in order to:
    //  1) notify the task machine to cancel the task, and:
    //  2) cancel the upload with managedUpload.abort()
    const onTaskCancel = (task: UnknownTask): void => {
        const managedUpload = managedUploads?.current?.[task.taskId];

        if (managedUpload) {
            managedUpload.abort();
            delete managedUploads.current[task.taskId];

            if (!task.error && !task.done) {
                addToast({
                    content: `Detection upload canceled for: ${task?.input?.filename}`,
                    kind: 'warning-banner',
                });
            }
        }
    };

    const { getToken } = useTokenModal();

    const handleFiles = useCallback(
        ({
            files,
            gapAnalysisId,
            onGroupStopped,
        }: HandleDetectionUploadInput): void => {
            if (files.length > 0) {
                const filesWithWrongType = [];
                const allowedFiles = files.filter((file: File): boolean => {
                    if (
                        !hasAcceptedFileType(
                            file,
                            SUPPORTED_DETECTION_FILE_TYPES,
                        )
                    ) {
                        filesWithWrongType.push(file);

                        return false;
                    }

                    return true;
                });

                if (filesWithWrongType.length > 0) {
                    addToast({
                        RenderContent: AddedWrongFileType,
                        renderContentInput: {
                            acceptedFileTypes: SUPPORTED_DETECTION_FILE_TYPES,
                            filesWithWrongType: filesWithWrongType.length,
                        },
                        kind: 'warning-card',
                    });
                } else if (allowedFiles.length > 0) {
                    if (allowedFiles.length > MAX_DETECTION_UPLOAD_BATCH_SIZE) {
                        addToast({
                            content:
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .UPLOAD_DETECTIONS.WARNINGS
                                    .ONE_FILE_AT_A_TIME,
                            kind: 'warning-card',
                        });
                    } else {
                        const taskCreators = allowedFiles.map(
                            (file: File): TaskCreator<FilenameInput> => {
                                const taskId = uuid();

                                return {
                                    taskId,
                                    onTaskStart: (
                                        bindOnTaskCancel: BindOnTaskCancel,
                                    ): void => {
                                        bindOnTaskCancel(onTaskCancel);

                                        taskUploadDetectionsToTrqkGap({
                                            variables: {
                                                bucketName,
                                                bucketRegion,
                                                file,
                                                gapAnalysisId,
                                                identityPoolId,
                                                taskId,
                                                trqkDirectoryUrl,
                                            },
                                            context: {
                                                bindManagedUpload: (
                                                    managedUpload: S3.ManagedUpload,
                                                ): void => {
                                                    if (
                                                        managedUploads?.current
                                                    ) {
                                                        managedUploads.current[
                                                            taskId
                                                        ] = managedUpload;
                                                    }
                                                },
                                                generateS3Key: (
                                                    currentFile: File,
                                                ): string =>
                                                    generateCSVFileKeyForS3({
                                                        baseNameFallback:
                                                            'detections',
                                                        file: currentFile,
                                                        userId,
                                                    }),
                                                getToken,
                                                sendTaskEvent,
                                            },
                                        });
                                    },
                                    input: {
                                        filename: file.name,
                                    },
                                };
                            },
                        );

                        sendTaskEvent({
                            type: 'CREATE_GROUP',
                            value: {
                                groupId: TASK_GROUP_ID_DETECTION_UPLOADS,
                                taskCreators,
                                onGroupStopped,
                            },
                        });
                    }
                }
            }
        },
        [addToast, cache, managedUploads.current, userId],
    );

    const compareUploadTasks = (
        prevTasks: UploadDetectionsTask[],
        nextTasks: UploadDetectionsTask[],
    ): boolean => _isEqual(prevTasks, nextTasks);

    const taskGroupSelector = (
        state: State<TaskContext, TaskEvent, TaskStateSchema>,
    ): UnknownTask[] => {
        if (state?.context?.tasks) {
            return Array.from(state.context.tasks.values()).filter(
                (task: UnknownTask): boolean =>
                    task.groupId === TASK_GROUP_ID_DETECTION_UPLOADS &&
                    !task.canceled, // canceled tasks will still exist until the entire task
                // group stops
            );
        }

        return [];
    };

    const tasks = useSelector(
        taskMachineService,
        // There's a tricky issue with type definitions in the task machine where specific task
        // types will conflict with UnknownTask, but it is safe in practice to ignore this here
        // eslint-disable-next-line
        // @ts-ignore
        taskGroupSelector,
        compareUploadTasks,
    );

    const inProgressTasks = useMemo(
        () =>
            tasks.filter((task: UploadDetectionsTask): boolean => {
                const error = task?.error ?? null;
                const done = task?.done ?? false;

                return !done && error === null;
            }),
        [tasks],
    );

    return (
        <UploadDetectionsContext.Provider
            value={{
                handleFiles,
                inProgressTasks,
                tasks,
            }}
        >
            {children}
        </UploadDetectionsContext.Provider>
    );
};
